import type { MailingAddress, Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { type Address } from "react-daum-postcode";

import type { TIntlAddressForm } from "../types/address";
import { formatPhone } from "./format";

export const BLOCK_ADDRESS = [
  { zip: "05842", roadname_address: "송파구 송파대로 55", lotnumber_address: "서울 송파구 장지동 875" }, // 크림
  { zip: "07983", roadname_address: "양천구 목동동로 363", lotnumber_address: "서울 양천구 목동 906" }, // 솔드아웃
  { zip: "22769", roadname_address: "서구 원석로 196", lotnumber_address: "인천 서구 원창동 488" }, // 로지스허브
];

/**
 * @description country, province 코드 찾기
 */
export const findCodes = ({
  formData,
  country,
  province,
}: {
  formData?: TIntlAddressForm;
  country: string;
  province: string;
}): { countryCode: string; provinceCode: string } => {
  let countryCode = "";
  let provinceCode = "";

  if (!formData) return { countryCode, provinceCode };

  for (const key in formData) {
    if (formData[key].name === country) {
      countryCode = key;

      if (formData[key].states?.[province]) {
        provinceCode = formData[key].states[province];
      }
    }
  }

  return { countryCode, provinceCode };
};

/**
 * @description 주소에 모든 필수 키에 대한 값이 있는지 확인
 */
export const isValidAddress = (address?: MailingAddress, lang = "ko"): boolean => {
  if (!address) return false;

  const requiredKeys: (keyof MailingAddress)[] = ["firstName", "lastName", "phone", "address1"];

  if (lang === "ko") {
    requiredKeys.push("province", "zip");
  }

  return requiredKeys.every((key) => Object.prototype.hasOwnProperty.call(address, key) && address[key]);
};

/**
 * @description 국가에 맞는 주소 형태로 변경 + 예외처리
 */
export const formatOrderAddress = ({
  address,
  lang,
}: {
  address?: Maybe<MailingAddress>;
  lang: "ko" | "en" | "jp";
}) => {
  const formatName = (firstName: string, lastName: string): string => {
    return lang === "ko" ? `${lastName}${firstName}` : `${firstName} ${lastName}`;
  };

  const formatCountry = (country: string): string => {
    return lang === "ko" ? "" : country;
  };

  return {
    name: formatName(address?.firstName || "", address?.lastName || ""),
    country: formatCountry(address?.country || ""),
    city: address?.city || "",
    address1: address?.address1 || "",
    address2: address?.address2 || "",
    province: addressToDisplayKorean(address?.province),
    provinceCode: address?.provinceCode || "",
    firstName: address?.firstName || "",
    lastName: address?.lastName || "",
    phone: formatPhone(address?.phone || ""),
    zip: String(address?.zip || ""),
  };
};

export interface IAddresses {
  data: {
    locationGroupZones: {
      edges: {
        zone: {
          id: string;
          name: string;
          countries: {
            id: string;
            name: string;
            code: {
              countryCode: string;
            };
          }[];
        };
        rateDefinitions: {
          edges: {
            node: {
              id: string;
              name: string;
              rateProvider: {
                id: string;
                price: {
                  amount: string;
                  currencyCode: string;
                };
              };
              methodConditions: {
                id: string;
                field: string;
                operator: string;
                conditionCriteria: {
                  unit: string;
                  value: number;
                };
              }[];
            };
          }[];
        };
      }[];
    };
  };
}

export const daumAddress = (data: Address): string => {
  const roadAddress = data.roadAddress.split(" ");
  return roadAddress.filter((value) => value !== data.sido).join(" ");
};

const PROVINCE_DATA = [
  { short: "서울", full: "서울특별시", en: "Seoul" },
  { short: "부산", full: "부산광역시", en: "Busan" },
  { short: "대구", full: "대구광역시", en: "Daegu" },
  { short: "인천", full: "인천광역시", en: "Incheon" },
  { short: "광주", full: "광주광역시", en: "Gwangju" },
  { short: "대전", full: "대전광역시", en: "Daejeon" },
  { short: "울산", full: "울산광역시", en: "Ulsan" },
  { short: "세종특별자치시", full: "세종특별자치시", en: "Sejong" },
  { short: "경기", full: "경기도", en: "Gyeonggi" },
  { short: "강원특별자치도", full: "강원특별자치도", en: "Gangwon" },
  { short: "충북", full: "충청북도", en: "Chungbuk" },
  { short: "충남", full: "충청남도", en: "Chungnam" },
  { short: "전북특별자치도", full: "전북특별자치도", en: "Jeonbuk" },
  { short: "전남", full: "전라남도", en: "Jeonnam" },
  { short: "경북", full: "경상북도", en: "Gyeongbuk" },
  { short: "경남", full: "경상남도", en: "Gyeongnam" },
  { short: "제주특별자치도", full: "제주특별자치도", en: "Jeju" },
  { short: "전남광주통합특별시", full: "전남광주통합특별시", en: "Gwangju" },
] as const;

/**
 * 시도명을 원하는 형태로 변환
 * @param outputType - 원하는 출력 형태 ('short' | 'full' | 'en')
 * @param input - 변환할 시도명 (어떤 형태든 가능)
 */
const convertProvince = (outputType: "short" | "full" | "en", input?: string | null): string => {
  if (!input?.trim()) return "";

  // 입력값과 일치하는 데이터 찾기
  const province = PROVINCE_DATA.find((p) => p.short === input || p.full === input || p.en === input);

  if (!province) return input; // 찾지 못하면 원본 반환

  return province[outputType];
};

// 한글을 영문으로 변환 - ProvinceNameEnMap
// ex) 부산광역시 -> Busan / 부산 -> Busan
export const addressToEnglish = (input?: string | null): string => {
  return convertProvince("en", input);
};

// 영어를 한글 단축명으로 변환
// ex) Busan -> 부산
export const addressToKorean = (input?: string | null): string => {
  return convertProvince("short", input);
};

// 영어를 한글 전체명으로 변환 - ProvinceNameKoMap
// ex) Busan -> 부산광역시
export const addressToFullKorean = (input?: string | null): string => {
  return convertProvince("full", input);
};

// 한글 단축명을 한글 전체명으로 변환
// ex) 부산 -> 부산광역시
export const addressShortToFull = (input?: string | null): string => {
  return convertProvince("full", input);
};

/**
 * @description 전남광주통합특별시 출범(2026.7.1) 이후에도 Shopify/ISO 3166-2는 아직 통합 코드가 없어 구(舊) 광주/전남 코드를 사용해야 함
 * Daum이 내려주는 sido="전남광주통합특별시"만으로는 구분이 불가능하므로 우편번호(zonecode) 대역으로 구 지역을 역추정
 *
 * 구 광주광역시: 61xxx
 * 구 전라남도: 57xxx ~ 59xxx
 *
 * TODO: ISO 3166-2:KR이 통합 코드를 정식 발급하고 Shopify가 이를 반영하면 이 함수와 onSubmit 내 호출부를 제거하고 addressToEnglish(formData.province)로 되돌라기
 */
export const resolveLegacyKoreaProvince = (zonecode: string): { short: string; full: string; en: string } | null => {
  const prefix = zonecode.slice(0, 2);

  if (prefix === "61") {
    return { short: "광주", full: "광주광역시", en: "Gwangju" };
  }

  if (["57", "58", "59"].includes(prefix)) {
    return { short: "전남", full: "전라남도", en: "Jeonnam" };
  }

  return null; // 매칭 안 되면 null 반환 (기존 sido 로직으로 폴백)
};

/**
 * @description 주문서 등에서 province를 화면 표시용 한글로 변환
 * 전남광주통합특별시 출범(2026.7.1) 이후에도 Shopify가 아직 통합 코드를 발급하지 않아, 저장된 주문 데이터의 province는 여전히 legacy 값("Jeonnam" / "Gwangju")으로 내려옴
 * 이 값들만 통합 명칭으로 강제 치환하고, 그 외에는 기존 addressToFullKorean(PROVINCE_DATA 매칭) 로직을 그대로 사용
 *
 * TODO: Shopify가 통합 코드를 정식 발급하면 LEGACY_MERGED_PROVINCES와 아래 분기를 제거하고 addressToFullKorean만 사용하도록 되돌리기
 */
const LEGACY_MERGED_PROVINCES = ["Jeonnam", "Gwangju"];

export const addressToDisplayKorean = (input?: string | null): string => {
  if (input && LEGACY_MERGED_PROVINCES.includes(input)) {
    return "전남광주통합특별시";
  }

  return addressToFullKorean(input);
};
